<script setup lang="ts">
import { observeConnectivity } from '@@/bits/connectivity'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { currentHostWithProtocol, getSearchParam } from '@@/bits/location'
import { COVER_PAGE_INDEX, SLIDESHOW_CONTENT_LOADED_ID } from '@@/bits/slideshow'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIndeterminateSpinner from '@@/library/v4/components/OzIndeterminateSpinner.vue'
import OzPadletLogo, {
  OzPadletLogoColorScheme,
  OzPadletLogoSizePreset,
} from '@@/library/v4/components/OzPadletLogo.vue'
import { slideshowMounted } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { useSurfaceStore } from '@@/pinia/surface'
import { TRANSLATION_QUERY_PARAM_KEY, useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { PageType } from '@@/types/slideshow'
import SurfaceQrCode from '@@/vuecomponents/SurfaceQrCode.vue'
import SurfaceSlideshowAuthor from '@@/vuecomponents/SurfaceSlideshowAuthor.vue'
import SurfaceSlideshowContent from '@@/vuecomponents/SurfaceSlideshowContent.vue'
import SurfaceSlideshowNavigationBarV2 from '@@/vuecomponents/SurfaceSlideshowNavigationBarV2.vue'
import { useSlideshowAttachments } from '@@/vuecomposables/slideshow_attachments'
import { useSlideshowLoadingState } from '@@/vuecomposables/slideshow_loading_state'
import { useSlideshowPages } from '@@/vuecomposables/slideshow_pages'
import { useSlideshowQRCode } from '@@/vuecomposables/slideshow_qr_code'
import { useSlideshowStyles } from '@@/vuecomposables/slideshow_styles'
import { useSurfaceFetchInitialState } from '@@/vuecomposables/surface_fetch_initial_state'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeMount, onMounted, ref, watch } from 'vue'

const surfaceStore = useSurfaceStore()
const { refresh, changeConnectivity } = surfaceStore
const { dir, isOnline, links, isEmbedded, isScreenshotMode, address, skinAccentTextColor, xAuthor } =
  storeToRefs(surfaceStore)
const { isSmallerThanDesktop3XL, safeAreaInsets } = storeToRefs(useWindowSizeStore())
const surfaceUserContributorsStore = useSurfaceUserContributorsStore()
const currentPostAuthor = computed(() => {
  const authorId = currentPost.value?.author_id || currentPost.value?.author_hashid
  return authorId ? surfaceUserContributorsStore.getUserById(authorId) : null
})

const surfaceTranslationsStore = useSurfaceTranslationsStore()

// STYLES

const { hasDarkBackgroundLuminance, primaryColorStyle, qrCodeBaseStyles, accentColorStyle } = useSlideshowStyles()

// PAGE LOGIC

const { isDataLoaded, hasLoadedPages, isPageCoverLoaded } = useSlideshowLoadingState()
const { pages, isFirstPage, currentPageIndex, currentPost } = useSlideshowPages()
const { isQrCodeShown, shouldRemoveQrCode, toggleQRCode } = useSlideshowQRCode()
const { areAttachmentsLoadedForCurrentPost } = useSlideshowAttachments()

/**
 * This watcher handles navigation to the correct page based on the url.
 * It covers 2 use cases:
 * 1. The initial load after data has been fetched and pages have been populated
 * 2. When the underlying padlet has been updated by realtime, the currentPageIndex needs to be recalculated
 */
watch(
  [isDataLoaded, pages],
  ([isDataLoaded, pages]) => {
    const isReadyToNavigate = isDataLoaded && pages.length > 0
    if (!isReadyToNavigate) {
      return
    }

    // fetch post authors only after posts are fetched, as we need the author id/hashid from the posts
    surfaceUserContributorsStore.fetchPostAuthors()

    // Jump to the correct index if there is any entity id in the url
    // Example: /slideshow/wish/123 or /slideshow/section/456
    const isWishPath = window.location.pathname.includes('/slideshow/wish/')
    const isSectionPath = window.location.pathname.includes('/slideshow/section/')
    if (!isWishPath && !isSectionPath) {
      currentPageIndex.value = COVER_PAGE_INDEX
      hasLoadedPages.value = true
      return
    }

    const splitPath = window.location.pathname.split('/')
    const entityIdFromPath = splitPath[splitPath.length - 1]
    const matchingEntityPageIndex = pages.findIndex(({ entityId, pageType }) => {
      // Matching entityId alone is insufficient because there could be collision with postId and sectionId
      // Assumption: Only two page types currently
      const isMatchingPageType = isWishPath ? pageType === PageType.Post : pageType === PageType.Section
      return isMatchingPageType && entityId === entityIdFromPath
    })

    // This only applies for sections since wishes can be moved to other padlets
    // and the backend will handle the redirection to the correct padlet based on the wishId
    const noMatchingPageFound = matchingEntityPageIndex === -1
    if (noMatchingPageFound) {
      window.history.replaceState({}, '', `/${address.value}/slideshow`)
      currentPageIndex.value = COVER_PAGE_INDEX
    } else {
      currentPageIndex.value = matchingEntityPageIndex
    }

    hasLoadedPages.value = true
  },
  { immediate: true },
)

const isCurrentPageContentLoaded = ref(false)
watch([hasLoadedPages, areAttachmentsLoadedForCurrentPost], ([loadedPages, attachmentsLoadedForCurrentPost]) => {
  if (!loadedPages || !attachmentsLoadedForCurrentPost || !isPageCoverLoaded) return
  nextTick(() => {
    setTimeout(() => {
      isCurrentPageContentLoaded.value = true
    }, 0)
  })
})

watch(
  isOnline,
  () => {
    // refresh the app automatically when the user comes back online
    // the store action checks for isOnline so we don't need to do it here
    refresh({ isAutoRefresh: true })
  },
  { immediate: true },
)

const surfaceSlideshowContentRef = ref<InstanceType<typeof SurfaceSlideshowContent>>()
const shouldShowPadletLogo = computed(() => !device.app)
const shouldShowPadletLogoWithBackground = computed(() => isEmbedded.value && isFirstPage.value)

const padletLogoSizePreset = computed(() =>
  isSmallerThanDesktop3XL.value ? OzPadletLogoSizePreset.W48px : OzPadletLogoSizePreset.W86px,
)
const avatarLogoSize = computed(() => (isSmallerThanDesktop3XL.value ? 14 : 24))
const padletLogoHref = computed(() => currentHostWithProtocol())

onBeforeMount(() => {
  useSurfaceFetchInitialState().fetchInitialState({
    postDrafts: false,
    submissionRequestLink: false,
  })
})

onMounted(async () => {
  // manually refresh once in the case where the user is visiting without any cache
  // then we watch for connectivity changes and refresh automatically
  refresh({ isAutoRefresh: false })
  observeConnectivity((isOnline: boolean): void => {
    changeConnectivity(isOnline)
  })

  if (isAppUsing('translatePadlet')) {
    const lang = getSearchParam(TRANSLATION_QUERY_PARAM_KEY)
    if (lang != null) {
      surfaceTranslationsStore.translatePadletFromBanner(lang)
    }
  }

  postMessage(slideshowMounted())
})
</script>

<template>
  <main :dir="dir" :class="['h-vh100', 'relative', 'flex flex-col justify-between']">
    <div
      v-if="shouldShowPadletLogo"
      :class="[
        'absolute',
        shouldShowPadletLogoWithBackground
          ? 'top-3 start-2.5'
          : 'top-4.5 start-5 desktop-3xl:top-9 desktop-3xl:start-10',
        'z-10', // Use z-index instead of modifying DOM order to preserve focus order (should be first focusable element)
        'flex items-center',
        isSmallerThanDesktop3XL ? 'gap-3' : 'gap-4',
        isSmallerThanDesktop3XL ? 'h-[17px]' : 'h-6',
      ]"
    >
      <OzPadletLogo
        v-if="shouldShowPadletLogoWithBackground"
        :class="['px-2.5 py-1.5 rounded-lg']"
        :dark-mode="hasDarkBackgroundLuminance"
        :href="padletLogoHref"
        :size-preset="padletLogoSizePreset"
        :style="{
          backgroundColor: accentColorStyle,
        }"
        :svg-style="{
          color: skinAccentTextColor,
        }"
      />
      <OzPadletLogo
        v-else
        :dark-mode="hasDarkBackgroundLuminance"
        :color-scheme="OzPadletLogoColorScheme.Text300"
        :href="padletLogoHref"
        :size-preset="padletLogoSizePreset"
      />
      <SurfaceSlideshowAuthor
        v-if="currentPost != null && xAuthor"
        :dark-mode="hasDarkBackgroundLuminance"
        :author="currentPostAuthor"
        :avatar-size="avatarLogoSize"
      />
    </div>
    <OzBaseButton
      v-if="isFirstPage"
      :class="[
        'absolute',
        'bg-white',
        'box-border',
        'overflow-hidden', // since the png qr code image background is not transparent, we want to hide the excess white portion
        (shouldRemoveQrCode || !isQrCodeShown) && 'invisible', // preload qr code when it is not shown yet
        'p-0',
        'z-10',
      ]"
      :style="{
        top: `${safeAreaInsets.top > 0 ? safeAreaInsets.top : qrCodeBaseStyles.baseDimension}px`,
        insetInlineEnd: `${qrCodeBaseStyles.baseDimension}px`,
        // OzBaseButton already has some default styles, so we need to override
        borderRadius: '8px',
        border: '4px solid black',
      }"
      data-testid="slideshowQrCode"
      @click="toggleQRCode"
      @keydown.enter="toggleQRCode"
      @keydown.space="toggleQRCode"
    >
      <SurfaceQrCode
        :src="links.slideshow_qr_code"
        :alt="__('Slideshow QR code')"
        :width="qrCodeBaseStyles.baseDimension * 4"
      />
    </OzBaseButton>
    <SurfaceSlideshowContent
      v-if="currentPageIndex != null"
      ref="surfaceSlideshowContentRef"
      :class="['flex-1']"
      :current-page-index="currentPageIndex"
      :overscan-page-limit="isScreenshotMode ? 0 : undefined"
    />
    <SurfaceSlideshowNavigationBarV2
      :current-page-element="surfaceSlideshowContentRef ? surfaceSlideshowContentRef.currentPageRef : undefined"
    />
    <div
      v-if="!hasLoadedPages"
      :class="['h-full w-full', 'flex justify-center items-center', 'absolute inset-0']"
      :style="{ backgroundColor: primaryColorStyle }"
    >
      <OzIndeterminateSpinner :dark-mode="hasDarkBackgroundLuminance" :size="40" />
    </div>
    <div
      v-if="isCurrentPageContentLoaded"
      :id="SLIDESHOW_CONTENT_LOADED_ID"
      data-testid="slideshowContentLoaded"
      :aria-hidden="true"
    />
  </main>
</template>
